<script setup lang="ts">
/**
 * Check · the family card (plan §3 step 2, §4.3, ui-spec §2.2): the shelf style the analysis
 * recognised, how sure it is ("90 % sure"; 40–69 % flagged "We're not sure this is the right
 * style"), why, and the runners-up as "Not this? Try…". Choosing a runner-up re-fits from the
 * same FitResult and is undoable. What the rebuild changed to make the layout buildable is shown
 * by RepairsCard.vue, below this card.
 */
import { computed } from 'vue';
import Icon from './Icon.vue';
import ProvChip from './ProvChip.vue';
import { app, snap, ui } from '@/app/state';
import { announce } from '@/app/announce';
import { chooseStyle } from '@/app/analysis';
import { confirmKey } from '@/app/edits';
import { focusSoon } from '@/app/navigation';
import { isFitResult, type FitResult } from '@/families/fit-schema';
import { getFamily, familyOf } from '@/families/registry';
import { designFromProposal, proposalFromFit } from '@/families/fit';
import { lowFidelity } from '@/app/fit-extras';

const fam = computed(() => familyOf(snap.value.design).definition);
const f = computed(() => snap.value.fields.family ?? null);
const fit = computed(() => ui.analysis.fit ?? null);
const result = computed<FitResult | null>(() => (isFitResult(ui.analysis.proposal) ? ui.analysis.proposal : null));
const manual = computed(() => ui.analysis.status === 'manual' || f.value?.source === 'manual');
const pct = computed(() => (fit.value ? Math.round(fit.value.confidence * 100) : null));
const why = computed(() => result.value?.candidates.find((c) => c.familyId === fit.value?.familyId)?.why ?? null);
const summary = computed(() => {
  const r = result.value;
  const id = fit.value?.familyId;
  const fm = id ? getFamily(id) : null;
  return r && fm && id && r.params[id] ? fm.fitSummary(r.params[id] as never) : null;
});
const runnersUp = computed(() => (fit.value?.runnersUp ?? []).filter((c) => getFamily(c.familyId)));
const needs = computed(() => !!f.value && ['proposed', 'published', 'unresolved'].includes(f.value.status));

function looksRight() {
  confirmKey('family', 'Style');
  announce(`${fam.value.label} confirmed.`);
  focusSoon('[data-confirm]:not([data-confirm="family"]), #dock [data-act="continue"]');
}
/** Re-fit from the same FitResult as another family; edits inside the old family do not carry over except W, H, D and material. */
function tryFamily(familyId: string) {
  const r = result.value;
  const fm = getFamily(familyId);
  if (!r || !fm) return;
  const pwf = proposalFromFit(r, familyId, { createdAt: new Date().toISOString() });
  const { design } = designFromProposal(pwf, app.store.stock);
  const keep = app.store.design;
  const next = { ...design, W: keep.W, H: keep.H, D: keep.D, stockProfileId: keep.stockProfileId, appearance: { ...keep.appearance } };
  app.store.apply(() => next, { label: `Style changed to ${fm.definition.label}` });
  announce(`Style changed to ${fm.definition.label}. Undo to go back.`);
}
</script>

<template>
  <section class="vcard family-card" :class="{ needs, 'is-flagged': fit?.flagged }" data-field="family" aria-labelledby="famTitle">
    <div class="vc-top">
      <h2 id="famTitle" class="vc-label">Style</h2>
      <span v-if="pct !== null" class="conf-chip" :class="fit?.flagged ? 'is-attn' : 'is-soft'" data-confidence>{{ pct }} % sure</span>
      <ProvChip v-if="f" :status="f.status" />
    </div>
    <p class="vc-value"><span class="fam-name">{{ fam.label }}</span><span v-if="summary" class="unit">{{ summary }}</span></p>
    <p class="muted small">{{ fam.description }}</p>
    <p v-if="fit?.flagged && !lowFidelity" class="vc-note fam-flag" role="note"><Icon name="question" /> We’re not sure this is the right style. Check the outline on the photo{{ runnersUp.length ? ', or try another style below' : '' }}.</p>
    <p v-if="lowFidelity" class="vc-note fam-fidelity" role="note" data-fidelity="low"><Icon name="question" /><span>This photo doesn’t fully match the {{ fam.label.toLowerCase() }} style; parts of it may be simplified.{{ lowFidelity.note ? ` ${lowFidelity.note.replace(/\.?$/, '.')}` : '' }}{{ runnersUp.length ? ' The closest other styles are below.' : '' }}</span></p>
    <p v-if="why" class="vc-ev"><Icon name="image" /><span>{{ why }}</span></p>
    <p v-else-if="manual" class="vc-ev"><Icon name="pencil" /><span>Set up by hand from this style, not read from a photo.</span></p>
    <div v-if="runnersUp.length" class="fam-try">
      <span class="muted small">Not this? Try…</span>
      <button v-for="c in runnersUp" :key="c.familyId" type="button" class="btn btn--sm btn--quiet" :data-try="c.familyId" @click="tryFamily(c.familyId)">{{ getFamily(c.familyId)!.definition.label }} · {{ Math.round(c.confidence * 100) }} %</button>
    </div>
    <div class="vc-acts">
      <button v-if="needs" id="confirm-family" type="button" class="btn btn--sm btn--soft" data-confirm="family" @click="looksRight"><Icon name="check" /> Looks right</button>
      <button type="button" class="btn btn--sm btn--quiet" data-act="other-style" @click="chooseStyle">Not this style</button>
    </div>
  </section>
</template>
